import * as THREE from 'three';
import {
  CubeFace,
  TileCoordinate,
  ViewportBounds,
  CameraViewport,
  TileInfo,
  LODLevel,
} from './Types';

export class ViewportCalculator {
  private static readonly CUBE_FACES = [
    CubeFace.FRONT,
    CubeFace.BACK,
    CubeFace.LEFT,
    CubeFace.RIGHT,
    CubeFace.UP,
    CubeFace.DOWN,
  ];

  /**
   * Calculate which tiles are visible based on camera viewport
   */
  public static getVisibleTiles(
    viewport: CameraViewport,
    lodLevel: LODLevel,
    tilePadding: number = 1,
  ): TileInfo[] {
    const visibleTiles: TileInfo[] = [];
    const tilesPerFace = lodLevel.tilesPerFace;

    // For LOD level 1, always load all faces regardless of camera orientation
    // This ensures the base level is always fully available
    const facesToLoad = lodLevel.level === 1 ? this.CUBE_FACES : this.getVisibleFaces(viewport);

    for (const face of facesToLoad) {
      const faceTiles = this.getVisibleTilesForFace(
        face,
        viewport,
        tilesPerFace,
        lodLevel.level,
        tilePadding,
      );
      visibleTiles.push(...faceTiles);
    }

    return visibleTiles;
  }

  /**
   * Determine which cube faces are visible from the current camera position
   */
  private static getVisibleFaces(viewport: CameraViewport): CubeFace[] {
    const visibleFaces: CubeFace[] = [];
    const { horizontalAngle, verticalAngle, fov } = viewport;

    // Convert angles to radians
    const hAngle = THREE.MathUtils.degToRad(horizontalAngle);
    const vAngle = THREE.MathUtils.degToRad(verticalAngle);
    const fovRad = THREE.MathUtils.degToRad(fov);

    // Calculate camera direction vector
    const direction = new THREE.Vector3(
      Math.sin(hAngle) * Math.cos(vAngle),
      Math.sin(vAngle),
      Math.cos(hAngle) * Math.cos(vAngle),
    );

    // TEMPORARY FIX: Force include upper face when looking up
    const isLookingUp = verticalAngle > 45; // Looking up significantly
    const isLookingDown = verticalAngle < -45; // Looking down significantly

    if (isLookingUp) {
      visibleFaces.push(CubeFace.UP);
    }

    if (isLookingDown) {
      visibleFaces.push(CubeFace.DOWN);
    }

    // Check each face
    for (const face of this.CUBE_FACES) {
      if (this.isFaceVisible(face, direction, fovRad)) {
        if (!visibleFaces.includes(face)) {
          // Avoid duplicates
          visibleFaces.push(face);
        }
      }
    }

    return visibleFaces;
  }

  /**
   * Check if a specific cube face is visible from the camera direction
   */
  private static isFaceVisible(
    face: CubeFace,
    cameraDirection: THREE.Vector3,
    fovRad: number,
  ): boolean {
    const faceNormal = this.getFaceNormal(face);
    const dot = cameraDirection.dot(faceNormal);

    // Face is visible if camera is looking towards it (considering FOV)
    const threshold = Math.cos(fovRad / 2 + 0.2); // Add buffer
    const isVisible = dot > -threshold;

    return isVisible;
  }

  /**
   * Get the normal vector for a cube face
   */
  private static getFaceNormal(face: CubeFace): THREE.Vector3 {
    switch (face) {
      case CubeFace.FRONT:
        return new THREE.Vector3(0, 0, 1);
      case CubeFace.BACK:
        return new THREE.Vector3(0, 0, -1);
      case CubeFace.LEFT:
        return new THREE.Vector3(-1, 0, 0);
      case CubeFace.RIGHT:
        return new THREE.Vector3(1, 0, 0);
      case CubeFace.UP:
        return new THREE.Vector3(0, 1, 0);
      case CubeFace.DOWN:
        return new THREE.Vector3(0, -1, 0);
      default:
        return new THREE.Vector3(0, 0, 1);
    }
  }

  /**
   * Get visible tiles for a specific cube face
   */
  private static getVisibleTilesForFace(
    face: CubeFace,
    viewport: CameraViewport,
    tilesPerFace: number,
    level: number,
    padding: number,
  ): TileInfo[] {
    const tiles: TileInfo[] = [];
    const bounds = this.calculateFaceViewportBounds(face, viewport, tilesPerFace);

    // Calculate which tiles are in the viewport (with padding)
    const minX = Math.max(0, Math.floor(bounds.minX) - padding);
    const maxX = Math.min(tilesPerFace - 1, Math.ceil(bounds.maxX) + padding);
    const minY = Math.max(0, Math.floor(bounds.minY) - padding);
    const maxY = Math.min(tilesPerFace - 1, Math.ceil(bounds.maxY) + padding);

    // Generate tile coordinates
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const coordinate: TileCoordinate = { x, y, face, level };
        const tileInfo: TileInfo = {
          ...coordinate,
          url: '', // URL will be generated by TileManager based on metadata
          priority: this.calculateTilePriority(coordinate, viewport),
          isVisible: this.isTileInViewport(coordinate, bounds),
          distance: this.calculateTileDistance(coordinate, viewport),
        };
        tiles.push(tileInfo);
      }
    }

    return tiles;
  }

  /**
   * Calculate viewport bounds on a specific cube face
   */
  private static calculateFaceViewportBounds(
    face: CubeFace,
    viewport: CameraViewport,
    tilesPerFace: number,
  ): ViewportBounds {
    // Convert camera direction to face-local coordinates
    const { horizontalAngle, verticalAngle, fov } = viewport;

    // FIXED: For higher LOD levels (3 and 4), load ALL tiles per face for maximum detail
    // The user wants full coverage at high zoom levels, not just viewport-limited tiles
    const level = this.calculateLevelFromTilesPerFace(tilesPerFace);

    if (level >= 3) {
      // For level 3 (4x4=16 tiles) and level 4 (8x8=64 tiles), load ALL tiles
      return {
        minX: 0,
        maxX: tilesPerFace - 1,
        minY: 0,
        maxY: tilesPerFace - 1,
        face,
      };
    }

    // For lower levels (1 and 2), use FOV-based calculation to optimize loading
    const fovFactor = Math.tan(THREE.MathUtils.degToRad(fov / 2));
    const centerX = tilesPerFace / 2;
    const centerY = tilesPerFace / 2;
    const extentX = (tilesPerFace * fovFactor) / 2;
    const extentY = (tilesPerFace * fovFactor) / 2;

    return {
      minX: Math.max(0, centerX - extentX),
      maxX: Math.min(tilesPerFace - 1, centerX + extentX),
      minY: Math.max(0, centerY - extentY),
      maxY: Math.min(tilesPerFace - 1, centerY + extentY),
      face,
    };
  }

  /**
   * Calculate LOD level from tilesPerFace
   * Level 1: 1x1, Level 2: 2x2, Level 3: 4x4, Level 4: 8x8
   */
  private static calculateLevelFromTilesPerFace(tilesPerFace: number): number {
    return Math.log2(tilesPerFace) + 1;
  }

  /**
   * Check if a tile is within the viewport bounds
   */
  private static isTileInViewport(coordinate: TileCoordinate, bounds: ViewportBounds): boolean {
    return (
      coordinate.x >= bounds.minX &&
      coordinate.x <= bounds.maxX &&
      coordinate.y >= bounds.minY &&
      coordinate.y <= bounds.maxY
    );
  }

  /**
   * Calculate tile priority based on distance from viewport center
   */
  private static calculateTilePriority(
    coordinate: TileCoordinate,
    viewport: CameraViewport,
  ): number {
    // Compute camera direction
    const hAngle = THREE.MathUtils.degToRad(viewport.horizontalAngle);
    const vAngle = THREE.MathUtils.degToRad(viewport.verticalAngle);
    const cameraDir = new THREE.Vector3(
      Math.sin(hAngle) * Math.cos(vAngle),
      Math.sin(vAngle),
      Math.cos(hAngle) * Math.cos(vAngle),
    ).normalize();

    // Estimate tile center direction vector for its cube face
    const tilesPerFace = 1 << (coordinate.level - 1);
    const u = ((coordinate.x + 0.5) / tilesPerFace) * 2 - 1; // [-1,1]
    const v = ((coordinate.y + 0.5) / tilesPerFace) * 2 - 1; // [-1,1]

    const tileDir = this.getDirectionForFace(coordinate.face, u, v).normalize();

    // Angle between camera direction and tile center
    const dot = THREE.MathUtils.clamp(cameraDir.dot(tileDir), -1, 1);
    const angleRad = Math.acos(dot);
    const angleDeg = THREE.MathUtils.radToDeg(angleRad);

    // Field-of-view weighting
    const fovRad = THREE.MathUtils.degToRad(viewport.fov);
    const insideFOV = angleRad <= fovRad / 2;

    // Priority model: strongly prefer tiles inside FOV and near the center
    // Higher LOD levels get boosted priority for FOV tiles
    const lodBoost = coordinate.level >= 3 ? coordinate.level * 200 : 0;
    let priority = (insideFOV ? 2000 + lodBoost : 800) - angleDeg * 12; // steeper drop-off
    
    // Extra boost for tiles very close to camera center (within 10 degrees)
    if (insideFOV && angleDeg < 10) {
      priority += 500 * (1 - angleDeg / 10); // Boost up to 500 for center tiles
    }

    // Ensure positive integer priority
    return Math.max(1, Math.floor(priority));
  }

  /**
   * Convert face-local (u,v) in [-1,1] to approximate world direction for that cube face
   */
  private static getDirectionForFace(face: CubeFace, u: number, v: number): THREE.Vector3 {
    switch (face) {
      case CubeFace.FRONT: // +Z
        return new THREE.Vector3(u, v, 1);
      case CubeFace.BACK: // -Z
        return new THREE.Vector3(-u, v, -1);
      case CubeFace.LEFT: // -X
        return new THREE.Vector3(-1, v, u);
      case CubeFace.RIGHT: // +X
        return new THREE.Vector3(1, v, -u);
      case CubeFace.UP: // +Y
        return new THREE.Vector3(u, 1, -v);
      case CubeFace.DOWN: // -Y
        return new THREE.Vector3(u, -1, v);
      default:
        return new THREE.Vector3(u, v, 1);
    }
  }

  /**
   * Calculate distance from tile to viewport center
   */
  private static calculateTileDistance(
    coordinate: TileCoordinate,
    viewport: CameraViewport,
  ): number {
    // Simplified distance calculation
    // In practice, this would calculate 3D distance from camera to tile center
    const centerX = coordinate.x + 0.5;
    const centerY = coordinate.y + 0.5;

    // Normalize to [-1, 1] range and calculate distance
    const normalizedX = (centerX / (1 << coordinate.level)) * 2 - 1;
    const normalizedY = (centerY / (1 << coordinate.level)) * 2 - 1;

    return Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
  }

  /**
   * Determine appropriate LOD level based on FOV
   */
  public static calculateLODLevel(fov: number, lodLevels: LODLevel[]): LODLevel {
    // Find the appropriate LOD level based on FOV thresholds
    for (const level of lodLevels) {
      if (fov >= level.fovThreshold.min && fov <= level.fovThreshold.max) {
        return level;
      }
    }

    // Fallback to the first level if no match
    return (
      lodLevels[0] || {
        level: 1,
        tilesPerFace: 1,
        fovThreshold: { min: 0, max: 180 },
        priority: 1,
      }
    );
  }

  /**
   * Debug method to test viewport tile calculation for different levels
   */
  public static debugViewportCalculation(): void {
    const testViewport: CameraViewport = {
      horizontalAngle: 0,
      verticalAngle: 0,
      fov: 5, // Very low FOV for level 4
      aspect: 1.0,
      visibleFaces: [
        CubeFace.FRONT,
        CubeFace.BACK,
        CubeFace.LEFT,
        CubeFace.RIGHT,
        CubeFace.UP,
        CubeFace.DOWN,
      ],
    };

    const testLODLevels: LODLevel[] = [
      { level: 1, tilesPerFace: 1, fovThreshold: { min: 60, max: 120 }, priority: 1 },
      { level: 2, tilesPerFace: 2, fovThreshold: { min: 30, max: 60 }, priority: 2 },
      { level: 3, tilesPerFace: 4, fovThreshold: { min: 15, max: 30 }, priority: 3 },
      { level: 4, tilesPerFace: 8, fovThreshold: { min: 0, max: 15 }, priority: 4 },
    ];

    for (const lodLevel of testLODLevels) {
      const visibleTiles = this.getVisibleTiles(testViewport, lodLevel, 0);

      // Count tiles per face
      const faceStats = new Map<CubeFace, number>();
      for (const tile of visibleTiles) {
        faceStats.set(tile.face, (faceStats.get(tile.face) || 0) + 1);
      }

      if (lodLevel.level === 4) {
        const maxTilesPerFace = Math.max(...Array.from(faceStats.values()));
      }
    }
  }
}
